import React, { useState } from "react"
import { Upload, UploadProps } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { RcFile } from "antd/es/upload/interface"
import { UploadRequestOption as RcCustomRequestOptions } from "rc-upload/lib/interface"

import { useDataImport } from "features/DataImport/api/useDataImport"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import { useProjectStore } from "stores/projectStore"
import { calculateFileUploadDuration } from "utils/files/calculateFileUploadDuration"
import { DATAFILE_TYPE } from "types/dataImport.types"

// Define the props interface
interface DataImportProps {
  name: string
  refresh: () => Promise<void> | void
  type: DATAFILE_TYPE
}

/**
 * Button Component for importing data files.
 *
 * This component allows users to upload files by clicking or dragging them to the upload area.
 * Supported file formats are CSV, JSON, and DQD (DataQ binary format).
 *
 * @param props - The component props
 * @returns {JSX.Element} The rendered data import component.
 */
const DataImport: React.FC<DataImportProps> = ({ name, refresh, type }) => {
  const { uploadDataImportFile, isUploadingDataimportFile } = useDataImport()
  const project = useProjectStore((state) => state.project)
  const [isLoadingModalVisible, setIsLoadingModalVisible] =
    useState<boolean>(false)
  const [modalDuration, setModalDuration] = useState<number>(10000)

  /**
   * Custom request handler for file upload
   */
  const customRequest = async ({
    file,
    onSuccess,
    onError,
  }: RcCustomRequestOptions<unknown>): Promise<void> => {
    try {
      if (typeof file === "string") {
        throw new Error("File upload from string path is not supported")
      }

      const response = await uploadDataImportFile(
        project.id,
        project.Created_by,
        type,
        file
      )
      onSuccess?.(response)
      refresh()
    } catch (error) {
      onError?.(error as Error)
      refresh()
    }
  }

  /**
   * Validates the file format before upload.
   *
   * @param file - The file to be uploaded.
   * @returns - Returns true if the file format is valid, otherwise returns `Upload.LIST_IGNORE`.
   */
  const beforeUpload = (file: RcFile): boolean | typeof Upload.LIST_IGNORE => {
    // DQD files are binary and may have empty or octet-stream MIME type
    const isDqdFile = type === DATAFILE_TYPE.DQD

    // Uses MIME format to validate file type (skip for DQD binary files)
    const isCorrectFormat =
      isDqdFile ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "application/json" ||
      file.type === "text/csv"

    if (!isCorrectFormat) {
      notify.toast.error(`${file.name} is not a valid file format.`)
    }

    const correctExtension =
      (type === DATAFILE_TYPE.CSV && file.name.endsWith("data.csv")) ||
      (type === DATAFILE_TYPE.MAP && file.name.endsWith("map.csv")) ||
      (type === DATAFILE_TYPE.JSON && file.name.endsWith(".json")) ||
      (type === DATAFILE_TYPE.DQD && file.name.endsWith(".dqd"))

    if (!isCorrectFormat || !correctExtension) {
      notify.toast.error(
        `${file.name} is not a valid file format or has an incorrect file extension.`
      )
    }
    return (isCorrectFormat && correctExtension) || Upload.LIST_IGNORE
  }

  /**
   * Handles upload status changes
   */
  const handleChange: UploadProps["onChange"] = (info): void => {
    if (info.file.status === "uploading") {
      // Dynamically set the modal duration based on the original file size (before compression)
      const dynamicDuration = calculateFileUploadDuration(info.file.size || 0)
      setModalDuration(dynamicDuration)
      setIsLoadingModalVisible(true)
    }
    if (info.file.status === "done") {
      setIsLoadingModalVisible(false)
      notify.toast.success(`${info.file.name} file uploaded successfully`)
      refresh()
    } else if (info.file.status === "error") {
      setIsLoadingModalVisible(false)
      notify.toast.error(`${info.file.name} file upload failed.`)
    }
  }

  const uploadProps: UploadProps = {
    name: "file",
    customRequest,
    beforeUpload,
    onChange: handleChange,
    showUploadList: false,
    maxCount: 1,
  }

  return (
    <>
      <ProgressModal
        title={"Uploading File..."}
        isLoading={isLoadingModalVisible}
        duration={modalDuration} // Set the modal duration dynamically
      />
      <Upload {...uploadProps} className="ml-auto">
        <DataQButton type="primary" isLoading={isUploadingDataimportFile}>
          {name}
        </DataQButton>
      </Upload>
    </>
  )
}

export default DataImport
